import { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-triggered reveal component.
 * Uses IntersectionObserver (native, no dependencies) + CSS transitions.
 *
 * Variants:
 *   - "zoom"        → scale-in from 0.9 with 2s ease-out (used for text/titles)
 *   - "fade-left"   → slide in from the left (used for images on the LEFT side)
 *   - "fade-right"  → slide in from the right (used for images on the RIGHT side)
 *   - "fade-up"     → slide up from below (subtle default)
 *
 * The animation runs ONCE (unobserve after first intersection).
 *
 * Props:
 *   - variant      → animation type
 *   - delay        → optional delay in ms (staggering)
 *   - as           → tag to render (default: div)
 *   - className    → extra classes
 *   - rootMargin   → observer rootMargin (default: "0px 0px -80px 0px" = trigger 80px before entering viewport)
 */
export default function Reveal({
  children,
  variant = "fade-up",
  delay = 0,
  as: Tag = "div",
  className = "",
  rootMargin = "0px 0px -80px 0px",
  duration,
  ...rest
}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    // Respect user's reduced-motion preference: skip animations entirely.
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin, threshold: 0.05 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  // Compute initial / final transforms per variant
  const base = "will-change-transform will-change-opacity";
  const timing = duration || (variant === "zoom" ? 2000 : 1000);
  const styleBase = {
    transitionProperty: "opacity, transform",
    transitionDuration: `${timing}ms`,
    transitionTimingFunction: variant === "zoom" ? "cubic-bezier(.16,.72,.14,1)" : "cubic-bezier(.22,.61,.36,1)",
    transitionDelay: `${delay}ms`,
  };

  let initial, final;
  switch (variant) {
    case "zoom":
      initial = { opacity: 0, transform: "scale(0.92)" };
      final = { opacity: 1, transform: "scale(1)" };
      break;
    case "fade-left":
      initial = { opacity: 0, transform: "translate3d(-40px,0,0)" };
      final = { opacity: 1, transform: "translate3d(0,0,0)" };
      break;
    case "fade-right":
      initial = { opacity: 0, transform: "translate3d(40px,0,0)" };
      final = { opacity: 1, transform: "translate3d(0,0,0)" };
      break;
    case "fade-up":
    default:
      initial = { opacity: 0, transform: "translate3d(0,24px,0)" };
      final = { opacity: 1, transform: "translate3d(0,0,0)" };
      break;
  }

  return (
    <Tag
      ref={ref}
      className={`${base} ${className}`}
      style={{ ...styleBase, ...(inView ? final : initial) }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
